/**
 * Search History Panel
 * SCR-106: Saved Searches & Search History
 *
 * Panel component displaying user's search history with clear option.
 * Replaces localStorage-based recent searches with API-backed history.
 *
 * @requirement BRD-000 Foundational Screens
 * @screen SCR-106 Search Interface
 * @iso27001 A.18.1.4 Privacy and protection of PII
 */

import { useState } from 'react';
import { Icon, Spinner } from '@myvault/shared/components';
import {
  useSearchHistory,
  useClearSearchHistory,
  type SearchHistoryEntry,
} from '../hooks/useSearchPersistence';
import { formatDistanceToNow } from 'date-fns';
import { nl } from 'date-fns/locale';

interface SearchHistoryPanelProps {
  onSelectSearch: (query: string) => void;
  limit?: number;
}

export function SearchHistoryPanel({ onSelectSearch, limit = 10 }: SearchHistoryPanelProps) {
  const [showConfirmClear, setShowConfirmClear] = useState(false);

  const { data, isLoading, error } = useSearchHistory(limit);
  const clearMutation = useClearSearchHistory();

  const handleClearHistory = async () => {
    try {
      await clearMutation.mutateAsync();
      setShowConfirmClear(false);
    } catch {
      console.error('Failed to clear search history');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Spinner size="sm" />
        <span className="ml-2 text-sm text-mv-gray-500">Laden...</span>
      </div>
    );
  }

  if (error) {
    return null; // Silently fail - history is not critical
  }

  const historyItems = data?.items || [];

  if (historyItems.length === 0) {
    return null; // Don't show empty history section
  }

  return (
    <div className="px-4 py-3">
      {/* Header */}
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-medium text-mv-gray-500">
          Recente zoekopdrachten
        </span>
        {showConfirmClear ? (
          <div className="flex items-center gap-2">
            <span className="text-xs text-mv-gray-500">Wissen?</span>
            <button
              onClick={handleClearHistory}
              disabled={clearMutation.isPending}
              className="text-xs text-mv-red-600 hover:text-mv-red-700"
            >
              {clearMutation.isPending ? '...' : 'Ja'}
            </button>
            <button
              onClick={() => setShowConfirmClear(false)}
              className="text-xs text-mv-gray-500 hover:text-mv-gray-700"
            >
              Nee
            </button>
          </div>
        ) : (
          <button
            onClick={() => setShowConfirmClear(true)}
            className="text-xs text-mv-gray-400 hover:text-mv-gray-600"
          >
            Wis alles
          </button>
        )}
      </div>

      {/* History list */}
      <ul className="space-y-1">
        {historyItems.map((entry) => (
          <SearchHistoryItem
            key={entry.id}
            entry={entry}
            onSelect={() => onSelectSearch(entry.query)}
          />
        ))}
      </ul>
    </div>
  );
}

interface SearchHistoryItemProps {
  entry: SearchHistoryEntry;
  onSelect: () => void;
}

function SearchHistoryItem({ entry, onSelect }: SearchHistoryItemProps) {
  const timeAgo = formatDistanceToNow(new Date(entry.searchedAt), {
    addSuffix: true,
    locale: nl,
  });

  return (
    <li>
      <button
        onClick={onSelect}
        className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left hover:bg-mv-gray-50"
      >
        <Icon
          name="clock"
          variant="outline"
          size="sm"
          className="flex-shrink-0 text-mv-gray-400"
        />
        <div className="min-w-0 flex-1">
          <span className="block truncate text-mv-gray-700">{entry.query}</span>
          <span className="text-xs text-mv-gray-400">
            {entry.resultCount} resultaten · {timeAgo}
          </span>
        </div>
      </button>
    </li>
  );
}

export default SearchHistoryPanel;
